import { parseApiResponse } from '@/shared/utils/apiResponse';
import { apiGet, apiPatch, apiPost } from '@/shared/utils/requestBuilder';

import type {
  ConteudoCarta,
  CreateModeloCartaDto,
  ModeloCarta,
  UpdateModeloCartaDto,
} from '../types/modeloCartaTypes';

const BASE = '/admin/arbitral/letter-templates';

// DTO do backend (em inglês). Convertido para ModeloCarta (PT-BR) no boundary (regra de tipos).
interface LetterTemplateContentDTO {
  title: string;
  body: string;
  deadlineText: string;
  closingText: string;
  includeQrCode: boolean;
}

interface LetterTemplateDTO {
  id: string;
  name: string;
  description: string | null;
  baseTemplate: string;
  content: LetterTemplateContentDTO;
  active: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

const toModeloCarta = (dto: LetterTemplateDTO): ModeloCarta => ({
  id: dto.id,
  nome: dto.name,
  descricao: dto.description,
  ativo: dto.active,
  versaoAtual: dto.version,
  templateId: 'carta_ar_notificacao',
  conteudo: {
    titulo: dto.content.title,
    corpo: dto.content.body,
    textoPrazo: dto.content.deadlineText,
    textoEncerramento: dto.content.closingText,
    incluirQrCode: dto.content.includeQrCode,
  },
  criadoEm: dto.createdAt,
  atualizadoEm: dto.updatedAt,
});

const toContentPayload = (conteudo: ConteudoCarta): LetterTemplateContentDTO => ({
  title: conteudo.titulo,
  body: conteudo.corpo,
  deadlineText: conteudo.textoPrazo,
  closingText: conteudo.textoEncerramento,
  includeQrCode: conteudo.incluirQrCode,
});

export const modelosCartaService = {
  async listar(): Promise<ModeloCarta[]> {
    const data = await parseApiResponse<{ items: LetterTemplateDTO[] }>(await apiGet(BASE));
    return data.items.map(toModeloCarta);
  },

  async buscar(id: string): Promise<ModeloCarta> {
    return toModeloCarta(await parseApiResponse<LetterTemplateDTO>(await apiGet(`${BASE}/${id}`)));
  },

  async criar(data: CreateModeloCartaDto): Promise<ModeloCarta> {
    const payload = {
      name: data.nome,
      description: data.descricao,
      content: toContentPayload(data.conteudo),
    };
    return toModeloCarta(await parseApiResponse<LetterTemplateDTO>(await apiPost(BASE, payload)));
  },

  async atualizar(id: string, data: UpdateModeloCartaDto): Promise<ModeloCarta> {
    const payload: Record<string, unknown> = {};
    if (data.nome !== undefined) payload.name = data.nome;
    if (data.descricao !== undefined) payload.description = data.descricao;
    if (data.conteudo !== undefined) payload.content = toContentPayload(data.conteudo);
    return toModeloCarta(
      await parseApiResponse<LetterTemplateDTO>(await apiPatch(`${BASE}/${id}`, payload)),
    );
  },

  async alternarStatus(id: string): Promise<ModeloCarta> {
    return toModeloCarta(
      await parseApiResponse<LetterTemplateDTO>(await apiPatch(`${BASE}/${id}/status`)),
    );
  },
};
